import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { notification, Tabs, Modal } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { v1 } from 'uuid';
// css
import { css } from 'emotion';
import { ArrowBackIcon } from 'mdi-react';
import colors from '../../../style/colors';

import TopBar from '../../ui/TopBar';
import Page from '../../ui/Page';
import getBackButtonUrl from '../../../utilities/get-back-button-url';
import getPageFromId from '../../../utilities/get-page-from-id';
import ActionWrapper from '../../ui/ActionWrapper';
import pageNavigator from '../../../utilities/page-navigator';
import DropDown from '../../ui/DropDown';
import InlineSpinner from '../../ui/InlineSpinner';
import usePageAndBackButtonURL from '../../../hooks/usePageAndBackButtonURL';

import { List, Tag, Typography, Button, DatePicker } from 'antd';
import { Box, TextField, MenuItem, FormGroup, FormControlLabel, Checkbox, Divider, Grid } from '@mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

import req from '../../../utilities/request-utility';
import SubmitAutomationComponent from './SubmitAutomationComponent';
import { useDropdownData } from '../hooks';

import moment from 'moment';

const draftListStyles = css`
	&.draft-list .ant-list-item {
		display: flex;
		align-items: flex-start;
		padding: 12px 16px;
		gap: 12px;
	}
	&.draft-list .ant-list-item-meta {
		flex: 1;
		min-width: 0;
	}
	&.draft-list .ant-list-item-meta-title {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		margin-bottom: 2px;
	}
	&.draft-list .ant-list-item-action {
		margin-left: 0;
		flex-shrink: 0;
		align-self: center;
	}
	&.draft-list .ant-list-item-action > li {
		padding: 0;
		margin: 0;
	}
`;

const buttonWrapStyles = { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', minHeight: 36 };
const titleWrapStyles = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' };
const DraftList = props => {
	const { data, loading, onEdit, onSubmit, onDelete, deletingId } = props;

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
				<InlineSpinner />
			</Box>
		);
	}

	if (!data || data.length === 0) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
				<Typography sx={{ color: '#999' }}>No drafts available</Typography>
			</Box>
		);
	}

	return (
		<List
			className={`draft-list ${draftListStyles}`}
			bordered
			dataSource={data}
			renderItem={item => {
				const dateRange = `${moment(item.tripStartDate).format('MM-DD-YYYY')} - ${moment(
					item.tripEndDate
				).format('MM-DD-YYYY')}`;

				// Check if all days are submitted
				const allDaysSubmitted = item.submittedCount === item.totalDays && item.totalDays > 0;
				const noDaysSubmitted = item.submittedCount === 0;
				const isDeleting = deletingId === item.id;

				const subtitle = `${item.totalDays} days • ${item.submittedCount} submitted`;

				const editButton = (
					<Button
						size="small"
						onClick={() => onEdit(item)}
						disabled={isDeleting}
						style={{ minHeight: 36, minWidth: 56 }}>
						{allDaysSubmitted ? 'View' : 'Edit'}
					</Button>
				);
				const submitButton = !allDaysSubmitted && (
					<Button
						size="small"
						onClick={() => onSubmit(item)}
						disabled={isDeleting}
						style={{ minHeight: 36, minWidth: 64 }}>
						Submit
					</Button>
				);
				const deleteButton = noDaysSubmitted && onDelete && (
					<Button
						size="small"
						danger
						onClick={() => onDelete(item)}
						disabled={isDeleting}
						loading={isDeleting}
						style={{ minHeight: 36, minWidth: 64 }}>
						{isDeleting ? 'Deleting…' : 'Delete'}
					</Button>
				);

				const actions = [
					<div key="actions" style={buttonWrapStyles}>
						{editButton}
						{submitButton}
						{deleteButton}
					</div>
				];

				return (
					<List.Item
						actions={actions}
						style={{
							cursor: 'pointer',
							backgroundColor: allDaysSubmitted ? '#f1f8f4' : 'transparent'
						}}
						key={item.id}>
						<List.Item.Meta
							title={
								<span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
									<span style={titleWrapStyles} title={dateRange}>
										{dateRange}
									</span>
									{allDaysSubmitted && (
										<span style={{ color: '#2e7d32', fontSize: '0.85em', flexShrink: 0 }}>
											✓ Complete
										</span>
									)}
								</span>
							}
							description={
								<>
									<div style={{ lineHeight: 1.4 }}>{subtitle}</div>
									<div style={{ fontSize: '0.85em', color: '#999', marginTop: 2 }}>
										Created: {moment(item.dateCreated).format('MM-DD-YYYY HH:mm')}
									</div>
								</>
							}
						/>
					</List.Item>
				);
			}}
		/>
	);
};

// utilities

const DKAutomations = props => {
	const { page, backButtonURL } = usePageAndBackButtonURL(props);

	const [automationData, setAutomationData] = useState({
		employeeId: '',
		location: '',
		foreman: '',
		tripStartDate: null,
		tripEndDate: null,
		cycleLength: 14,
		project: '',
		subProject: '',
		activity: '',
		trade: ''
	});

	// State to store the daily schedule data
	const [dailySchedule, setDailySchedule] = useState([]);

	// State for foremen
	const [foremen, setForemen] = useState([]);
	const [foremenLoading, setForemenLoading] = useState(true);

	// State for form submission
	const [submittingForm, setSubmittingForm] = useState(false);

	// Flag to prevent auto-generation when loading a draft
	const [isLoadingDraft, setIsLoadingDraft] = useState(false);

	// Track the current automation ID being edited
	const [currentAutomationId, setCurrentAutomationId] = useState(null);

	// State for submit modal
	const [submitModalVisible, setSubmitModalVisible] = useState(false);
	const [selectedAutomationForSubmit, setSelectedAutomationForSubmit] = useState(null);

	// State for delete in progress
	const [deletingId, setDeletingId] = useState(null);

	// Fetch automations using react-query
	const {
		data: { data: automations = [] } = {},
		isFetching: automationsLoading,
		refetch: refetchAutomations
	} = useQuery('automations', () => req()(`semcotime/automations`), {
		keepPreviousData: true,
		refetchOnWindowFocus: false,
		refetchOnMount: true, // Refetch when navigating back (e.g. after month change) so drafts list is up to date
		staleTime: 30 * 1000 // 30 seconds
	});

	// Fetch foremen data
	const getForemen = () => {
		setForemenLoading(true);

		req()(`semcotime/foremen/`)
			.then(({ data }) => {
				const assignedIds = data.assigned.map(d => d.id);

				setForemen([
					...data.assigned,
					{ disabled: true, id: '', name: '------------------' },
					...data.all.filter(d => !assignedIds.includes(d.id))
				]);
				setForemenLoading(false);
			})
			.catch(err => {
				console.error('Error fetching foremen:', err);
				setForemenLoading(false);
			});
	};

	// Fetch foremen on component mount
	useEffect(() => {
		getForemen();
	}, []);

	// Fetch DAB locations using react-query
	const { data: { data: dabLocations = [] } = {}, isFetching: dabLocationsIsFetching } = useQuery(
		'dabLocations',
		() => req()(`semcotime/dab-locations`),
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 60 * 60 * 1000
		}
	);

	// Prepare formData for useDropdownData hook
	const formDataForDropdowns = {
		externalId: automationData.employeeId || '',
		project: automationData.project,
		subProject: automationData.subProject,
		date: automationData.tripStartDate
			? moment(automationData.tripStartDate).format('YYYY-MM-DD')
			: moment().format('YYYY-MM-DD')
	};

	// Fetch dropdown data using the hook
	const {
		dabTradeCodes,
		dabTradeIsFetching,
		projectData,
		projectStatus,
		projectActivityData,
		projectActivityStatus,
		subProjectData,
		subProjectStatus
	} = useDropdownData(formDataForDropdowns);

	// Time registration types with IFS mapping
	const timeRegistrationsTypes = [
		{ label: 'Work Time', value: 1, IfsValue: 'WorkTime' },
		{ label: 'Standby', value: 2, IfsValue: 'Standby' },
		{ label: 'Paid Rest', value: 3, IfsValue: 'PaidRest' },
		{ label: 'Driving Hours', value: 4, IfsValue: 'DrivingHours' },
		{ label: 'Heli Out', value: 5, IfsValue: 'HeliTimeOut' },
		{ label: 'Heli Home', value: 6, IfsValue: 'HeliTimeHome' },
		{ label: 'Waiting Hours', value: 7, IfsValue: 'WaitingHours' },
		// Boat Location
		{ label: 'Mobilization', value: 8, IfsValue: 'Mobilization' },
		{ label: 'Demobilization', value: 9, IfsValue: 'Demobilization' },
		{ label: 'Crew Change', value: 10, IfsValue: 'CrewChange' },
		{ label: 'WorkTime Boat', value: 11, IfsValue: 'NormalDay' },
		{ label: 'Waiting on weather', value: 12, IfsValue: 'WaitingWeather' },
		//Travel Abroad
		{ label: 'Travelling Abroad', value: 13, IfsValue: 'TravellingAbroad' }
	];

	// Filter time registration types based on selected location
	const getAvailableTimeTypes = () => {
		if (!automationData.location || dabLocations.length === 0) {
			return timeRegistrationsTypes;
		}

		const locationRules = dabLocations.find(d => d.DabLocationValue === automationData.location);
		if (!locationRules) {
			return timeRegistrationsTypes;
		}

		// Filter types based on location IFS rules
		return timeRegistrationsTypes.filter(type => {
			const ruleValue = locationRules[type.IfsValue];
			return ruleValue && JSON.parse(ruleValue);
		});
	};

	// Function to check if shift is night or day based on start and end times
	const checkShift = (startTime, endTime) => {
		const momentStartTime = moment(startTime, 'HH:mm');
		const momentEndtime = moment(endTime, 'HH:mm');

		// If end time is same or before start time, it's a night shift (crosses midnight)
		if (momentEndtime.isSameOrBefore(momentStartTime)) return 'Night';

		return 'Day';
	};

	// Handler for location change - reset data except emp id, location, and foreman
	const handleLocationChange = newLocation => {
		// Check if there's existing data that would be lost
		const hasExistingData = automationData.tripStartDate || automationData.tripEndDate || dailySchedule.length > 0;

		const proceedWithChange = () => {
			setAutomationData({
				employeeId: automationData.employeeId, // keep
				location: newLocation, // update
				foreman: automationData.foreman, // keep
				tripStartDate: null, // reset
				tripEndDate: null, // reset
				cycleLength: 14 // reset to default
			});
			setDailySchedule([]); // clear schedule
		};

		// Show warning if there's existing data
		if (hasExistingData && automationData.location !== newLocation) {
			Modal.confirm({
				title: 'Location Change Warning',
				content: 'Changing the location will reset all dates and the daily schedule. Do you want to continue?',
				okText: 'Yes, Change Location',
				cancelText: 'Cancel',
				onOk: () => {
					proceedWithChange();
				},
				onCancel: () => {
					// Keep the current location - do nothing
				}
			});
		} else {
			// No existing data, proceed without warning
			proceedWithChange();
		}
	};

	// Generate initial daily schedule when start date and cycle length are set
	useEffect(() => {
		const generateSchedule = () => {
			// Skip if loading a draft
			if (isLoadingDraft) {
				return;
			}

			// Wait for start date and end date to have the time
			if (!automationData.tripStartDate || !automationData.tripEndDate) {
				setDailySchedule([]);
				return;
			}

			// Convert moment objects to native Date
			const startMoment = moment.isMoment(automationData.tripStartDate)
				? automationData.tripStartDate
				: moment(automationData.tripStartDate);

			const endMoment = moment.isMoment(automationData.tripEndDate)
				? automationData.tripEndDate
				: moment(automationData.tripEndDate);

			// Use cycle length to determine number of days
			const daysDiff = automationData.cycleLength;

			// Extract time from trip dates (start time from start date, end time from end date)
			const startTime = startMoment.format('HH:mm');
			const endTime = endMoment.format('HH:mm');

			// Determine shift type based on times
			const shiftType = checkShift(startTime, endTime);

			// Get available time types for the selected location
			const availableTypes = getAvailableTimeTypes();

			// Helper function to get a valid time type based on preference
			const getValidTimeType = preferredLabel => {
				// Check if preferred type is available
				const preferred = availableTypes.find(t => t.label === preferredLabel);
				if (preferred) return preferred.label;

				// Fallback: use first available type
				return availableTypes.length > 0 ? availableTypes[0].label : 'Work Time';
			};

			const schedule = Array.from({ length: daysDiff }, (_, index) => {
				const currentMoment = startMoment.clone().add(index, 'days');

				const dayNumber = index + 1;
				let timeType = 'Work Time';

				// Set time type based on day position
				if (dayNumber === 1) {
					timeType = getValidTimeType('Heli Out');
				} else if (dayNumber === daysDiff) {
					timeType = getValidTimeType('Heli Home');
				} else {
					timeType = getValidTimeType('Work Time');
				}

				return {
					id: v1(),
					day: dayNumber,
					date: currentMoment.format('YYYY-MM-DD'),
					timeType: timeType,
					activityShortname: '',
					trade: '',
					shift: shiftType,
					startTime: startTime,
					endTime: endTime
				};
			});
			setDailySchedule(schedule);
		};

		generateSchedule();
	}, [automationData.tripStartDate, automationData.tripEndDate, automationData.cycleLength]);

	// Handler for updating time type in the schedule
	const handleTimeTypeChange = (dayNumber, newTimeType) => {
		setDailySchedule(prev => prev.map(day => (day.day === dayNumber ? { ...day, timeType: newTimeType } : day)));
	};

	// Handler for updating project (applies to all days)
	const handleProjectChange = newProject => {
		setAutomationData(prev => ({
			...prev,
			project: newProject,
			subProject: '', // Clear subProject when project changes
			activity: '' // Clear activity when project changes
		}));
	};

	// Handler for updating subProject (applies to all days)
	const handleSubProjectChange = newSubProject => {
		setAutomationData(prev => ({
			...prev,
			subProject: newSubProject,
			activity: '' // Clear activity when subProject changes
		}));
	};

	// Handler for updating activity (applies to all days)
	const handleActivityChange = newActivity => {
		setAutomationData(prev => ({
			...prev,
			activity: newActivity
		}));
	};

	// Handler for updating trade (applies to all days)
	const handleTradeChangeGlobal = newTrade => {
		setAutomationData(prev => ({
			...prev,
			trade: newTrade
		}));
	};

	// Apply project/subProject/activity/trade to all days when they change
	useEffect(() => {
		if (dailySchedule.length > 0) {
			setDailySchedule(prev =>
				prev.map(day => ({
					...day,
					activityShortname:
						automationData.project && automationData.subProject && automationData.activity
							? `${automationData.project}.${automationData.subProject}.${automationData.activity}`
							: '',
					trade: automationData.trade || day.trade
				}))
			);
		}
	}, [automationData.project, automationData.subProject, automationData.activity, automationData.trade]);

	// Submit handler
	const handleSubmit = (action = 'draft') => {
		// Validate required fields
		if (!automationData.employeeId) {
			notification.error({
				duration: 5,
				message: 'Validation Error',
				description: 'Employee ID is required'
			});
			return;
		}

		if (!automationData.location) {
			notification.error({
				duration: 5,
				message: 'Validation Error',
				description: 'Location is required'
			});
			return;
		}

		if (!automationData.foreman) {
			notification.error({
				duration: 5,
				message: 'Validation Error',
				description: 'Foreman is required'
			});
			return;
		}

		if (!automationData.tripStartDate || !automationData.tripEndDate) {
			notification.error({
				duration: 5,
				message: 'Validation Error',
				description: 'Trip start and end dates are required'
			});
			return;
		}

		if (!automationData.project) {
			notification.error({
				duration: 5,
				message: 'Validation Error',
				description: 'Project is required'
			});
			return;
		}

		if (!automationData.subProject) {
			notification.error({
				duration: 5,
				message: 'Validation Error',
				description: 'Sub-Project is required'
			});
			return;
		}

		if (!automationData.activity) {
			notification.error({
				duration: 5,
				message: 'Validation Error',
				description: 'Activity is required'
			});
			return;
		}

		if (!automationData.trade) {
			notification.error({
				duration: 5,
				message: 'Validation Error',
				description: 'Trade is required'
			});
			return;
		}

		if (dailySchedule.length === 0) {
			notification.error({
				duration: 5,
				message: 'Validation Error',
				description: 'No daily schedule generated. Please check your dates and cycle length.'
			});
			return;
		}

		// Map timeType to type number
		const getTypeNumber = timeType => {
			const typeMap = {
				'Work Time': 1,
				Standby: 2,
				'Paid Rest': 3,
				'Driving Hours': 4,
				'Heli Out': 5,
				'Heli Home': 6,
				'Waiting Hours': 7
			};
			return typeMap[timeType] || 1;
		};

		// Prepare submission data - format each day as a complete registration object
		const processedSchedule = dailySchedule.map(day => {
			// Use project/subProject/activity from automationData (applied to all days)
			const project = automationData.project || '';
			const subProject = automationData.subProject || '';
			const activity = automationData.activity || '';
			const trade = automationData.trade || '';

			// Create time registration object for this day
			const timeRegistration = {
				id: v1(), // unique id
				type: getTypeNumber(day.timeType), // number value based on time type
				startTime: `${day.startTime}:00`, // convert HH:mm to HH:mm:ss
				endTime: `${day.endTime}:00`, // convert HH:mm to HH:mm:ss
				customerDetail: '', // default empty
				customerNote: '', // default empty
				changeShift: false, // default false
				freeTextCustomerNote: false, // false
				shift: day.shift.toLowerCase(), // 'Day' or 'Night' -> 'day' or 'night'
				approvedByForeman: false, // false
				savedByBlueCollar: true, // true (marking as saved)
				foremanChanges: {} // empty
			};

			return {
				id: day.id, // UUID from daily schedule
				date: day.date, // per date (YYYY-MM-DD)
				detailedReportingEnabled: false, // default
				location: automationData.location, // from dropdown
				project: project, // from automationData
				subProject: subProject, // from automationData
				activity: activity, // from automationData
				trade: trade, // from automationData
				foreman: automationData.foreman, // from dropdown
				endOfRotation: false, // false by default
				timeRegistrations: [timeRegistration], // array with one time registration
				nptHott: [], // empty by default
				type: 'time' // time by default
			};
		});

		const submissionData = {
			employeeId: automationData.employeeId,
			tripStartDate: moment(automationData.tripStartDate).format('YYYY-MM-DD HH:mm'),
			tripEndDate: moment(automationData.tripEndDate).format('YYYY-MM-DD HH:mm'),
			cycleLength: automationData.cycleLength,
			dailySchedule: processedSchedule,
			action: action // 'draft' or 'submit'
		};

		console.log(`${action === 'draft' ? 'Saving as draft' : 'Submitting'} automation data:`, submissionData);

		setSubmittingForm(action);

		if (action === 'draft') {
			// Determine if creating new or updating existing
			const isUpdating = !!currentAutomationId;
			const apiCall = isUpdating
				? req().put(`semcotime/automations/${currentAutomationId}`, submissionData)
				: req().post(`semcotime/automations`, submissionData);

			apiCall
				.then(response => {
					const { data } = response;

					// Build description with warnings if any errors
					let description = data.message || `Draft ${isUpdating ? 'updated' : 'saved'} successfully!`;
					if (data.errors && data.errors.length > 0) {
						description += `\n\nWarnings:\n${data.errors
							.map(err => `${err.date}: ${err.message}`)
							.join('\n')}`;
					}

					notification.success({
						duration: 8,
						message: isUpdating ? 'Draft Updated' : 'Draft Saved',
						description: description
					});
					setSubmittingForm(false);

					// Clear form state after successful save
					setCurrentAutomationId(null);
					setAutomationData({
						employeeId: '',
						location: '',
						foreman: '',
						tripStartDate: null,
						tripEndDate: null,
						cycleLength: 14,
						project: '',
						subProject: '',
						activity: '',
						trade: ''
					});
					setDailySchedule([]);

					// Refetch automations to update the list
					refetchAutomations();
				})
				.catch(err => {
					const { response = {} } = err;
					const errorData = response.data || {};

					let errorDescription = errorData.message || `Failed to ${isUpdating ? 'update' : 'save'} draft`;
					if (errorData.errors && errorData.errors.length > 0) {
						errorDescription += `:\n${errorData.errors
							.map(err => `${err.date}: ${err.message}`)
							.join('\n')}`;
					}

					notification.error({
						duration: 10,
						message: 'Error',
						description: errorDescription
					});
					setSubmittingForm(false);
				});
		}
	};

	// Handle editing a draft
	const handleEditDraft = draft => {
		// Set flag to prevent auto-generation
		setIsLoadingDraft(true);

		// Store the automation ID for updating
		setCurrentAutomationId(draft.id);

		// Map type number back to label
		const getTypeLabel = typeNumber => {
			const typeMap = {
				1: 'Work Time',
				2: 'Standby',
				3: 'Paid Rest',
				4: 'Driving Hours',
				5: 'Heli Out',
				6: 'Heli Home',
				7: 'Waiting Hours',
				8: 'Mobilization',
				9: 'Demobilization',
				10: 'Crew Change',
				11: 'WorkTime Boat',
				12: 'Waiting on weather',
				13: 'Travelling Abroad'
			};
			return typeMap[typeNumber] || 'Work Time';
		};

		// Populate automation data
		setAutomationData({
			employeeId: draft.employeeId,
			location: draft.dailySchedule[0]?.location || '',
			foreman: draft.dailySchedule[0]?.foreman || '',
			tripStartDate: moment(draft.tripStartDate),
			tripEndDate: moment(draft.tripEndDate),
			cycleLength: draft.cycleLength,
			project: draft.dailySchedule[0]?.project || '',
			subProject: draft.dailySchedule[0]?.subProject || '',
			activity: draft.dailySchedule[0]?.activity || '',
			trade: String(draft.dailySchedule[0]?.trade || '')
		});

		// Populate daily schedule
		const formattedSchedule = draft.dailySchedule.map((day, index) => {
			const activityShortname = `${day.project}.${day.subProject}.${day.activity}`;

			return {
				id: day.id,
				day: index + 1,
				date: day.date,
				timeType: getTypeLabel(day.timeRegistrations[0]?.type),
				activityShortname: activityShortname,
				trade: String(day.trade),
				shift:
					day.timeRegistrations[0]?.shift?.charAt(0).toUpperCase() +
						day.timeRegistrations[0]?.shift?.slice(1) || 'Day',
				startTime: day.timeRegistrations[0]?.startTime?.substring(0, 5) || '06:00',
				endTime: day.timeRegistrations[0]?.endTime?.substring(0, 5) || '18:00',
				isSubmitted: day.isSubmitted || false // Preserve the isSubmitted flag from backend
			};
		});

		setDailySchedule(formattedSchedule);

		// Reset flag after a short delay to allow manual editing
		setTimeout(() => {
			setIsLoadingDraft(false);
		}, 100);

		notification.info({
			duration: 4,
			message: 'Draft Loaded',
			description: `Draft with ${draft.totalDays} days loaded for editing`
		});
	};

	// Handle submitting a draft directly
	const handleSubmitDraft = draft => {
		setSelectedAutomationForSubmit(draft);
		setSubmitModalVisible(true);
	};

	// Handle delete draft (only allowed when no days submitted; backend enforces same rule)
	const handleDeleteDraft = draft => {
		Modal.confirm({
			title: 'Delete draft?',
			content:
				'This will permanently delete this draft and all its day entries. This cannot be undone.',
			okText: 'Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				setDeletingId(draft.id);
				try {
					await req().delete(`semcotime/automations/${draft.id}`);
					notification.success({
						duration: 5,
						message: 'Draft deleted',
						description: 'The draft and its entries have been deleted.'
					});
					if (currentAutomationId === draft.id) {
						setCurrentAutomationId(null);
						setAutomationData({
							employeeId: '',
							location: '',
							foreman: '',
							tripStartDate: null,
							tripEndDate: null,
							cycleLength: 14,
							project: '',
							subProject: '',
							activity: '',
							trade: ''
						});
						setDailySchedule([]);
					}
					refetchAutomations();
				} catch (err) {
					const msg =
						err?.response?.data?.message || err?.message || 'Failed to delete draft';
					notification.error({
						duration: 8,
						message: 'Delete failed',
						description: msg
					});
				} finally {
					setDeletingId(null);
				}
			}
		});
	};

	// Handle successful submission
	const handleSubmitSuccess = () => {
		setSubmitModalVisible(false);
		setSelectedAutomationForSubmit(null);

		// Clear form state
		setCurrentAutomationId(null);
		setAutomationData({
			employeeId: '',
			location: '',
			foreman: '',
			tripStartDate: null,
			tripEndDate: null,
			cycleLength: 14
		});
		setDailySchedule([]);

		refetchAutomations();
		notification.success({
			duration: 5,
			message: 'Automation Submitted',
			description: 'All work hours have been successfully registered'
		});
	};

	// Check if all days are submitted (to disable automation fields)
	const allDaysSubmitted = dailySchedule.length > 0 && dailySchedule.every(day => day.isSubmitted);
	const isSaving = !!submittingForm;
	const formDisabled = allDaysSubmitted || isSaving;

	console.log('🚀 ~ DKAutomations ~ dailySchedule:', dailySchedule);
	console.log('🚀 ~ All days submitted:', allDaysSubmitted);

	return (
		<Page className={componentStyles(props)}>
			<TopBar
				actionLeft={
					<ActionWrapper onClick={() => pageNavigator(backButtonURL, 'backward')}>
						<ArrowBackIcon />
					</ActionWrapper>
				}
				title={page.title}
			/>
			{/* main area */}
			<Grid container spacing={2} style={{ height: '100%' }}>
				<Grid
					item
					xs={12}
					md={4}
					style={{
						overflowY: 'auto',
						display: 'flex',
						flexDirection: 'column',
						height: '100vh',
						p: 4
					}}>
					<Box sx={{ width: '100%', height: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
						{/* Draft Area */}
						<Box
							sx={{
								flex: 1,
								overflowY: 'auto',
								border: '1px solid #ccc'
							}}>
							<DraftList
								data={Array.isArray(automations) ? automations : (automations?.data) ?? []}
								loading={automationsLoading}
								onEdit={handleEditDraft}
								onSubmit={handleSubmitDraft}
								onDelete={handleDeleteDraft}
								deletingId={deletingId}
							/>
						</Box>
					</Box>
				</Grid>
				<Grid item xs={12} md={8} sx={{ height: '100vh', overflowY: 'auto' }}>
					<Box sx={{ width: '100%', p: '0.75rem' }}>
						{' '}
						<Box
							component="form"
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start',
								gap: 2,
								mt: 2,
								ml: 2,
								pb: 4
							}}
							noValidate
							autoComplete="off">
							{/* Saving state banner with animation */}
							{submittingForm && (
								<Box
									sx={{
										width: '100%',
										mb: 2,
										p: 2,
										background: 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)',
										borderLeft: '4px solid #ff9800',
										borderRadius: '4px',
										display: 'flex',
										alignItems: 'center',
										gap: 2,
										animation: 'savingPulse 1.5s ease-in-out infinite',
										'@keyframes savingPulse': {
											'0%, 100%': { opacity: 1 },
											'50%': { opacity: 0.88 }
										}
									}}>
									<InlineSpinner />
									<Typography sx={{ fontWeight: 600, color: '#e65100' }}>
										{submittingForm === 'draft'
											? currentAutomationId
												? 'Updating draft...'
												: 'Saving draft...'
											: 'Saving...'}
									</Typography>
								</Box>
							)}
							{/* Edit Mode Indicator */}
							{currentAutomationId && !submittingForm && (
								<Box
									sx={{
										width: '100%',
										mb: 2,
										p: 2,
										background: '#e3f2fd',
										borderLeft: '4px solid #2196f3',
										borderRadius: '4px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between'
									}}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										<Typography sx={{ fontWeight: 600, color: '#1976d2' }}>
											✏️ Editing Draft (ID: {currentAutomationId})
										</Typography>
									</Box>
									<Button
										size="small"
										variant="outlined"
										onClick={() => {
											setCurrentAutomationId(null);
											setAutomationData({
												employeeId: '',
												location: '',
												foreman: '',
												tripStartDate: null,
												tripEndDate: null,
												cycleLength: 14
											});
											setDailySchedule([]);
										}}
										sx={{ minWidth: 100 }}>
										{allDaysSubmitted ? 'Close' : 'Cancel Edit'}
									</Button>
								</Box>
							)}
							{/* Row: Emp ID */}
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
								<Box sx={{ width: 140, fontWeight: 600 }}>Emp ID</Box>
								<TextField
									size="small"
									variant="outlined"
									sx={{ width: 250, background: formDisabled ? '#f5f5f5' : '#fff' }}
									InputProps={{ style: { height: 32 } }}
									inputProps={{ style: { padding: '4px 8px' } }}
									value={automationData.employeeId}
									onChange={e => setAutomationData({ ...automationData, employeeId: e.target.value })}
									placeholder="Enter Employee ID"
									disabled={formDisabled}
								/>
							</Box>
							{/* Row: Location */}
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
								<Box sx={{ width: 140, fontWeight: 600 }}>Location</Box>
								<TextField
									size="small"
									select
									variant="outlined"
									sx={{ width: 250, background: formDisabled ? '#f5f5f5' : '#fff' }}
									InputProps={{ style: { height: 32 } }}
									inputProps={{ style: { padding: '4px 8px' } }}
									value={automationData.location}
									onChange={e => handleLocationChange(e.target.value)}
									disabled={dabLocationsIsFetching || formDisabled}
									SelectProps={{
										displayEmpty: true,
										renderValue: selected => {
											if (!selected) {
												return (
													<span style={{ color: '#999' }}>
														{dabLocationsIsFetching
															? 'Loading locations...'
															: 'Select Location'}
													</span>
												);
											}
											return selected;
										}
									}}>
									<MenuItem value="">
										{dabLocationsIsFetching ? 'Loading locations...' : 'Select Location'}
									</MenuItem>
									{dabLocations.map(location => (
										<MenuItem key={location.DabLocationValue} value={location.DabLocationValue}>
											{location.DabLocationValue}
										</MenuItem>
									))}
								</TextField>
							</Box>
							{/* Row: Foreman */}
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
								<Box sx={{ width: 140, fontWeight: 600 }}>Foreman</Box>
								<TextField
									size="small"
									select
									variant="outlined"
									sx={{ width: 250, background: formDisabled ? '#f5f5f5' : '#fff' }}
									InputProps={{ style: { height: 32 } }}
									inputProps={{ style: { padding: '4px 8px' } }}
									value={automationData.foreman}
									onChange={e => setAutomationData({ ...automationData, foreman: e.target.value })}
									disabled={foremenLoading || formDisabled}
									SelectProps={{
										displayEmpty: true,
										renderValue: selected => {
											if (!selected) {
												return (
													<span style={{ color: '#999' }}>
														{foremenLoading ? 'Loading foremen...' : 'Select Foreman'}
													</span>
												);
											}
											const selectedForeman = foremen.find(f => f.id === selected);
											return selectedForeman ? selectedForeman.name : selected;
										}
									}}>
									<MenuItem value="">
										{foremenLoading ? 'Loading foremen...' : 'Select Foreman'}
									</MenuItem>
									{foremen.map(foreman => (
										<MenuItem key={foreman.id} value={foreman.id} disabled={foreman.disabled}>
											{foreman.name}
										</MenuItem>
									))}
								</TextField>
							</Box>
							{/* Row: Trip Start Date/Time */}
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
								<Box sx={{ width: 140, fontWeight: 600 }}>Trip Start Date/Time</Box>

								<DatePicker
									style={{ width: 250, background: formDisabled ? '#f5f5f5' : '#fff' }}
									showTime
									format="YYYY-MM-DD HH:mm"
									minuteStep={15}
									getPopupContainer={trigger => trigger.parentElement}
									placeholder=""
									value={automationData.tripStartDate}
									onChange={date => setAutomationData({ ...automationData, tripStartDate: date })}
									disabled={formDisabled}
								/>
							</Box>
							{/* Row: Trip End Date/Time */}
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
								<Box sx={{ width: 140, fontWeight: 600 }}>Trip End Date/Time</Box>
								<DatePicker
									style={{ width: 250, background: formDisabled ? '#f5f5f5' : '#fff' }}
									showTime
									format="YYYY-MM-DD HH:mm"
									minuteStep={15}
									getPopupContainer={trigger => trigger.parentElement}
									placeholder=""
									value={automationData.tripEndDate}
									onChange={date => setAutomationData({ ...automationData, tripEndDate: date })}
									disabled={formDisabled}
								/>
							</Box>
							{/* Row: Cycle Length (locked in edit mode to avoid changing day count) */}
							<Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
								<Box sx={{ width: 140, fontWeight: 600 }}>Cycle Length</Box>
								<FormGroup row>
									<FormControlLabel
										control={
											<Checkbox
												sx={{ p: 0.5 }}
												checked={automationData.cycleLength === 7}
												onChange={() =>
													setAutomationData({ ...automationData, cycleLength: 7 })
												}
												disabled={formDisabled || !!currentAutomationId}
											/>
										}
										label={<span style={{ fontWeight: 400, fontSize: 15 }}>7 Day</span>}
										sx={{ mr: 3 }}
									/>
									<FormControlLabel
										control={
											<Checkbox
												sx={{ p: 0.5 }}
												checked={automationData.cycleLength === 14}
												onChange={() =>
													setAutomationData({ ...automationData, cycleLength: 14 })
												}
												disabled={formDisabled || !!currentAutomationId}
											/>
										}
										label={<span style={{ fontWeight: 400, fontSize: 15 }}>14 Day</span>}
										sx={{ mr: 3 }}
									/>
									<FormControlLabel
										control={
											<Checkbox
												sx={{ p: 0.5 }}
												checked={automationData.cycleLength === 21}
												onChange={() =>
													setAutomationData({ ...automationData, cycleLength: 21 })
												}
												disabled={formDisabled || !!currentAutomationId}
											/>
										}
										label={<span style={{ fontWeight: 400, fontSize: 15 }}>21 Day</span>}
									/>
								</FormGroup>
							</Box>

							{/* Row: Project */}
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 2 }}>
								<Box sx={{ width: 140, fontWeight: 600 }}>Project</Box>
								<DropDown
									loading={projectStatus === 'loading'}
									onChange={e => handleProjectChange(e.target.value)}
									options={projectData.map(d => ({
										label: `${d.id} - ${d.name}`,
										value: d.id
									}))}
									placeholder="Select Project"
									value={automationData.project}
									disabled={formDisabled}
								/>
							</Box>

							{/* Row: Sub-Project */}
							{automationData.project && (
								<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
									<Box sx={{ width: 140, fontWeight: 600 }}>Sub-Project</Box>
									<DropDown
										loading={subProjectStatus === 'loading'}
										onChange={e => handleSubProjectChange(e.target.value)}
										options={subProjectData.map(d => ({
											label: `${d.id} - ${d.description}`,
											value: d.id
										}))}
										placeholder={
											subProjectStatus === 'loading'
												? 'Loading...'
												: subProjectData.length === 0
												? 'No subprojects found'
												: 'Select Sub-Project'
										}
										value={automationData.subProject}
										disabled={formDisabled}
									/>
								</Box>
							)}

							{/* Row: Activity */}
							{automationData.project && automationData.subProject && (
								<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
									<Box sx={{ width: 140, fontWeight: 600 }}>Activity</Box>
									<DropDown
										loading={projectActivityStatus === 'loading'}
										onChange={e => handleActivityChange(e.target.value)}
										options={projectActivityData.map(d => ({
											label: `${d.no} - ${d.description}`,
											value: d.id
										}))}
										placeholder={
											projectActivityStatus === 'loading'
												? 'Loading...'
												: projectActivityData.length === 0
												? 'No activities'
												: 'Select Activity'
										}
										value={automationData.activity}
										disabled={formDisabled}
									/>
								</Box>
							)}

							{/* Row: Trade */}
							<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
								<Box sx={{ width: 140, fontWeight: 600 }}>Trade</Box>
								<DropDown
									loading={dabTradeIsFetching}
									onChange={e => handleTradeChangeGlobal(e.target.value)}
									options={dabTradeCodes.map(d => ({
										label: `${d.DabTradeId} - ${d.DabTradeDesc}`,
										value: d.DabTradeId
									}))}
									placeholder="Select Trade"
									value={automationData.trade}
									disabled={formDisabled}
								/>
							</Box>

							<Divider sx={{ width: '100%', my: 2 }} />

							{/* List of Days */}
							{dailySchedule.length > 0 && (
								<Box sx={{ width: '100%', overflowY: 'auto', maxHeight: 400 }}>
									<TableContainer component={Paper} sx={{ border: '1px solid #e0e0e0' }}>
										<Table size="small">
											<TableHead>
												<TableRow sx={{ backgroundColor: '#f5f5f5' }}>
													<TableCell
														sx={{
															width: '10vw',
															fontWeight: 600,
															borderRight: '1px solid #e0e0e0'
														}}>
														Day
													</TableCell>
													<TableCell
														sx={{ fontWeight: 600, borderRight: '1px solid #e0e0e0' }}>
														Time Type
													</TableCell>
													<TableCell
														sx={{ fontWeight: 600, borderRight: '1px solid #e0e0e0' }}>
														Activity Shortname
													</TableCell>
													<TableCell
														sx={{ fontWeight: 600, borderRight: '1px solid #e0e0e0' }}>
														Trade
													</TableCell>
													<TableCell
														sx={{ fontWeight: 600, borderRight: '1px solid #e0e0e0' }}>
														Shift
													</TableCell>
													<TableCell
														sx={{ fontWeight: 600, borderRight: '1px solid #e0e0e0' }}>
														Start Time
													</TableCell>
													<TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{dailySchedule.map(day => (
													<TableRow
														key={day.day}
														sx={{
															'&:hover': { backgroundColor: '#f9f9f9' },
															backgroundColor: day.isSubmitted
																? '#e8f5e9'
																: 'transparent',
															opacity: day.isSubmitted ? 0.7 : 1
														}}>
														<TableCell
															sx={{
																fontWeight: 600,
																borderRight: '1px solid #e0e0e0',
																backgroundColor: day.isSubmitted ? '#c8e6c9' : '#fafafa'
															}}>
															Day {day.day}:
															{day.isSubmitted && (
																<span
																	style={{
																		marginLeft: '8px',
																		color: '#2e7d32',
																		fontSize: '0.85em'
																	}}>
																	✓ Submitted
																</span>
															)}
														</TableCell>
														<TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
															<TextField
																select
																variant="standard"
																value={day.timeType}
																onChange={e =>
																	handleTimeTypeChange(day.day, e.target.value)
																}
																disabled={day.isSubmitted || isSaving}
																sx={{ minWidth: 120 }}
																InputProps={{ disableUnderline: true }}>
																{getAvailableTimeTypes().map(type => (
																	<MenuItem key={type.value} value={type.label}>
																		{type.label}
																	</MenuItem>
																))}
															</TextField>
														</TableCell>
														<TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
															{automationData.project &&
															automationData.subProject &&
															automationData.activity
																? `${automationData.project}.${automationData.subProject}.${automationData.activity}`
																: '-'}
														</TableCell>
														<TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
															{automationData.trade || '-'}
														</TableCell>
														<TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
															{day.shift}
														</TableCell>
														<TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
															{day.startTime}
														</TableCell>
														<TableCell>{day.endTime}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Box>
							)}

							{/* Action Buttons */}
							{dailySchedule.length > 0 && (
								<>
									<Divider sx={{ width: '100%', my: 3 }} />
									{allDaysSubmitted ? (
										<Box sx={{ p: 2, background: '#e8f5e9', borderRadius: 1, textAlign: 'center' }}>
											<Typography sx={{ color: '#2e7d32', fontWeight: 600 }}>
												✓ All days have been submitted to IFS
											</Typography>
											<Typography sx={{ color: '#666', fontSize: '0.9em', mt: 1 }}>
												This automation is complete and can no longer be edited
											</Typography>
										</Box>
									) : (
										<Box
											sx={{
												display: 'flex',
												justifyContent: 'flex-end',
												gap: 2.5,
												mt: 2,
												mb: 4
											}}>
											<Button
												variant="contained"
												color="primary"
												onClick={() => handleSubmit('draft')}
												disabled={submittingForm}
												sx={{
													minWidth: 180,
													height: 42,
													fontSize: '0.95rem',
													fontWeight: 600,
													boxShadow: 2
												}}>
												{submittingForm === 'draft'
													? currentAutomationId
														? 'Updating...'
														: 'Saving...'
													: currentAutomationId
													? 'Update Draft'
													: 'Save as Draft'}
											</Button>
										</Box>
									)}
								</>
							)}
						</Box>
					</Box>
				</Grid>
			</Grid>

			{/* Submit Automation Modal */}
			<SubmitAutomationComponent
				visible={submitModalVisible}
				onClose={() => {
					setSubmitModalVisible(false);
					setSelectedAutomationForSubmit(null);
				}}
				automation={selectedAutomationForSubmit}
				onSuccess={handleSubmitSuccess}
			/>
		</Page>
	);
};

const componentStyles = isUser => css`
	background-color: ${colors.white} !important;

	.inline-scroll-wrapper {
		overflow-x: auto;
		overflow-y: auto;
		-webkit-overflow-scrolling: touch;

		padding: 1rem;

		.react-pdf__Page__canvas {
			margin: 0 auto !important;
		}
	}

	.main-content {
		display: flex;
		justify-content: space-between;
		background-color: ${colors.white};

		.col {
			border: 1px solid ${colors.lightGrey};
		}

		.col-left {
		}

		.col-right {
		}
	}

	.meta {
		color: ${colors.darkGrey};
		text-align: center;
	}
`;

export default DKAutomations;
